import React from "react";
import {
    Card,
    CardMedia,
    CardContent,
    Box,
    Typography,
    CardHeader,
    Chip,
    Grid,
    Avatar,
    Tooltip,
} from "@mui/material";

import "react-circular-progressbar/dist/styles.css";
import DayJS from "react-dayjs";

import ThisTheme from "../../../../themes/TMSTheme";
import SchoolIcon from "@mui/icons-material/School";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PhotoIcon from "@mui/icons-material/Photo";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import LaunchIcon from "@mui/icons-material/Launch";
import DescriptionIcon from "@mui/icons-material/Description";
import NotesIcon from "@mui/icons-material/Notes";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";

import ModuleEnrolmentDetails from "./ModuleEnrolmentDetails";
import ModuleMedia from "./ModuleMedia";
import ModuleEvents from "./ModuleEvents";
import SkillChip from "../SkillChip";

function Module({ module, isActive, onAssetDownload, onComplete, onRetake }) {
    const title = module?.name ?? "Course";
    const code = module?.code ?? "-";
    const image = module?.image ?? "";
    const validFrom = module?.validFrom ?? "";
    const type = module?.type?.name ?? "undefined";
    const mediaType = module?.media?.type ?? "undefined";

    let avatarIcon = <ErrorOutlineIcon />;
    let avatarStyle = {
        backgroundColor: "#FFF",
        color: ThisTheme.palette.primary.main,
    };
    let tooltip = "module";

    switch (type) {
        case "e-learning":
            avatarIcon = <LaunchIcon />;
            tooltip = "e-learning module";
            break;

        case "classroom":
            avatarIcon = <SchoolIcon />;
            tooltip = "classroom module";
            break;

        case "video":
            avatarIcon = <VideoLibraryIcon />;
            tooltip = "video module";
            break;

        case "audio":
            avatarIcon = <LibraryMusicIcon />;
            tooltip = "audio module";
            break;

        case "resource":
            switch (mediaType) {
                case "document":
                    avatarIcon = <DescriptionIcon />;
                    tooltip = "resource module (document)";
                    break;

                case "text":
                    avatarIcon = <NotesIcon />;
                    tooltip = "resource module (text)";
                    break;

                case "image":
                    avatarIcon = <PhotoIcon />;
                    tooltip = "resource module (image)";
                    break;

                default:
                    avatarIcon = <InsertDriveFileOutlinedIcon />;
                    tooltip = "resource module (empty)";
                    break;
            }
            break;

        default:
            break;
    }

    return (
        <Card className="card">
            {image.length > 16 && (
                <CardMedia
                    component="img"
                    height="300"
                    image={image}
                    alt={title}
                />
            )}
            <CardHeader
                disableTypography={true}
                avatar={
                    <Tooltip title={tooltip} arrow>
                        <Avatar
                            aria-label={title}
                            style={avatarStyle}
                            sx={{ width: 52, height: 52, marginRight: "4px" }}
                        >
                            {avatarIcon}
                        </Avatar>
                    </Tooltip>
                }
                title={
                    <Typography
                        id={`mod_${module.id}_title`}
                        component="h3"
                        className="card-header-title"
                    >
                        {title} - {code}
                    </Typography>
                }
                subheader={
                    <Typography
                        component="span"
                        className="card-header-subtitle"
                    >
                        Released on{" "}
                        <DayJS format="DD MMM YYYY">{validFrom}</DayJS>
                    </Typography>
                }
            />

            {!!module?.description && (
                <CardContent>
                    <Box className="card-content">
                        <Typography variant="h4" component="h4">
                            Description
                        </Typography>
                        <Typography variant="body1">
                            {module.description}
                        </Typography>
                    </Box>
                </CardContent>
            )}

            {!!module?.overview && (
                <CardContent>
                    <Box className="card-content">
                        <Typography variant="h4" component="h4">
                            Overview
                        </Typography>
                        <Typography variant="body1">
                            {module.overview}
                        </Typography>
                    </Box>
                </CardContent>
            )}

            {!!module?.requirement?.passMark >= 0 && (
                <CardContent>
                    <Box className="card-content">
                        This module
                        {!!module.requirement.passMark > 0
                            ? ` requires a pass mark of ${module.requirement.passMark}`
                            : " does not require a pass mark."}
                    </Box>
                </CardContent>
            )}

            {!!module?.skill && (
                <CardContent>
                    <Box className="card-content">
                        <Grid container spacing={3}>
                            <Grid item xs={6} sm={6} md={6}>
                                {module && module.skill && (
                                    <SkillChip label={module.skill.name} />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            )}

            {!!module?.assets?.length > 0 && (
                <CardContent>
                    <Box className="card-content">
                        <fieldset>
                            <legend>
                                <Typography variant="h4" component="h4">
                                    Associated files
                                    <span className="sr-only">
                                        &nbsp;for {title}
                                    </span>
                                </Typography>
                            </legend>
                            <Box>
                                {module.assets.map((asset) => (
                                    <Tooltip
                                        key={asset.id}
                                        title="download file"
                                        arrow
                                    >
                                        <Chip
                                            label={asset.name}
                                            aria-label={`Download file ${asset.name}`}
                                            className="chip"
                                            variant="outlined"
                                            color="primary"
                                            icon={
                                                <DownloadForOfflineIcon
                                                    sx={{ fontSize: 26 }}
                                                />
                                            }
                                            onClick={
                                                !!onAssetDownload &&
                                                (() => onAssetDownload(asset))
                                            }
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                        </fieldset>
                    </Box>
                </CardContent>
            )}

            {!!module?.trainers?.length > 0 && (
                <CardContent>
                    <Box className="card-content">
                        <fieldset>
                            <legend>
                                <Typography variant="h4" component="h4">
                                    Trainers
                                    <span className="sr-only">
                                        &nbsp;for {title}
                                    </span>
                                </Typography>
                            </legend>
                            <Box>
                                {module.trainers.map((trainer) => (
                                    <Chip
                                        key={trainer.id}
                                        variant="outlined"
                                        className="chip"
                                        label={trainer.name}
                                    />
                                ))}
                            </Box>
                        </fieldset>
                    </Box>
                </CardContent>
            )}

            {!!module?.url && (
                <CardContent>
                    <Box className="card-content">
                        <Typography variant="h4" component="h4">
                            Link
                        </Typography>
                        <Typography variant="body1">
                            <a
                                href={module.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {module.url}
                            </a>
                        </Typography>
                    </Box>
                </CardContent>
            )}

            {!!module?.enrolment && (
                <ModuleEnrolmentDetails module={module} onRetake={onRetake} />
            )}

            {!!module?.media && (
                <ModuleMedia
                    media={module.media}
                    isActive={isActive}
                    moduleId={module.id}
                    onComplete={onComplete}
                />
            )}

            {type === "classroom" && !!module && !!module.events && (
                <ModuleEvents
                    moduleId={module.id}
                    events={module.events}
                    isActive={isActive}
                    bookedEvent={module?.enrolment?.event}
                />
            )}
        </Card>
    );
}

export default Module;
